import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { OrgType, TenantDetail, TenantKind } from '@up2d8/contracts';
import { LoadingState } from '@up2d8/ui';
import { organisationsCsv } from '../../shared/billing';
import { saveBlob } from '../../shared/download';
import { useSession } from '../../shared/session';
import {
  AdminIcon,
  AdminPage,
  Badge,
  Btn,
  Card,
  Drawer,
  Empty,
  Field,
  Form,
  Input,
  Note,
  PageHead,
  SearchField,
  Select,
  Table,
  Toolbar,
  useAdminToast,
} from '../admin-kit';
import { FilterMenu } from './filter-menu';
import { useBilling } from './master-data';
import { UsersCell } from './subscriptions-page';
import { ORG_TYPES } from './org-type-card';
import { maandJaar, soortLabel, wanneer } from './platform-format';

type StatusFilter = 'all' | 'active' | 'suspended' | 'archived';
type SoortFilter = 'all' | 'customer' | TenantKind;
type TypeFilter = 'all' | OrgType;

/**
 * De bedrijvenlijst van het platformbeheer (P2): zoeken, filteren op status en soort, en een nieuw
 * bedrijf toevoegen in een paneel.
 *
 * Zoeken gebeurt op de server (naam of code); status en soort filteren in de lijst die er al is -
 * de API kent die filters niet, en het platform telt een handvol bedrijven, geen duizenden.
 */
export function TenantsPage() {
  const { t } = useTranslation();
  const { api } = useSession();
  const queryClient = useQueryClient();
  const toast = useAdminToast();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [soort, setSoort] = useState<SoortFilter>('all');
  const [orgType, setOrgType] = useState<TypeFilter>('all');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();

  const tenants = useQuery({
    queryKey: ['platform', 'tenants', search],
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/platform/tenants', {
        params: { query: search ? { search } : {} },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as { items: TenantDetail[] }).items;
    },
  });

  /*
    MST-3: aanmaken in één keer - de organisatie, dan de eerste vestiging en de eerste beheerder als
    die ingevuld zijn. Mislukt een van de laatste twee, dan bestaat de organisatie al: de melding
    zegt wat er nog ontbreekt, en het detailscherm opent, zodat het daar af te werken is.
  */
  const create = useMutation({
    mutationFn: async ({ tenant: input, site, admin }: NewTenantPlan) => {
      const { data, error } = await api.POST('/api/v1/platform/tenants', {
        body: input as never,
      });
      if (error || !data) throw error ?? new Error('Aanmaken mislukt');
      const tenant = data as TenantDetail;
      const params = { path: { tenantId: tenant.id } };
      const missing: string[] = [];
      if (site) {
        const result = await api.POST('/api/v1/platform/tenants/{tenantId}/sites', {
          params,
          body: site as never,
        });
        if (result.error) missing.push(t('master.create.site'));
      }
      if (admin) {
        const result = await api.POST('/api/v1/platform/tenants/{tenantId}/invitations', {
          params,
          body: { ...admin, roleHint: 'admin' } as never,
        });
        if (result.error) missing.push(t('master.create.admin'));
      }
      return { tenant, missing };
    },
    onSuccess: async ({ tenant, missing }) => {
      setDrawerOpen(false);
      toast.show(
        missing.length > 0
          ? t('master.create.partial', { name: tenant.name, missing: missing.join(', ') })
          : t('beheer.platform.tenants.created', { name: tenant.name }),
      );
      await queryClient.invalidateQueries({ queryKey: ['platform', 'tenants'] });
      void navigate({ to: '/master/organisaties/$tenantId', params: { tenantId: tenant.id } });
    },
  });

  const zichtbaar = (tenants.data ?? []).filter(
    (tenant) =>
      // "Alle" laat het archief weg (MST-2); wie het zoekt, kiest "Gearchiveerd".
      (status === 'all' ? tenant.status !== 'archived' : tenant.status === status) &&
      (soort === 'all' || (soort === 'customer' ? tenant.kind === null : tenant.kind === soort)) &&
      (orgType === 'all' || tenant.orgType === orgType),
  );

  // MST-5: pakket, gebruikers en activiteit per organisatie, uit dezelfde bron als Abonnementen.
  const billing = useBilling();
  const perTenant = new Map(billing.rows.map((row) => [row.tenant.id, row]));
  const exporteer = () => {
    const ids = new Set(zichtbaar.map((tenant) => tenant.id));
    // `allRows`: met filter "Gearchiveerd" bleef de export anders leeg.
    const rows = billing.allRows.filter((row) => ids.has(row.tenant.id));
    const stamp = new Date().toISOString().slice(0, 10);
    void saveBlob(
      new Blob([organisationsCsv(rows)], { type: 'text/csv;charset=utf-8' }),
      `organisaties-${stamp}.csv`,
    );
  };

  return (
    <AdminPage>
      <PageHead
        title={t('tenants.title')}
        actions={
          <>
            <Btn
              variant="sec"
              icon={<AdminIcon name="download" size={16} />}
              disabled={billing.pending}
              onClick={exporteer}
            >
              {t('master.list.export')}
            </Btn>
            <Btn
              icon={<AdminIcon name="plus" size={16} />}
              onClick={() => {
                create.reset();
                setDrawerOpen(true);
              }}
            >
              {t('tenants.new')}
            </Btn>
          </>
        }
      />

      <Card>
        <Toolbar>
          <SearchField label={t('tenants.search')} value={search} onChange={setSearch} />
          <FilterMenu<StatusFilter>
            label={t('tenants.status')}
            value={status}
            onChange={setStatus}
            options={[
              { value: 'all', label: t('beheer.platform.filters.all') },
              { value: 'active', label: t('tenants.active') },
              { value: 'suspended', label: t('tenants.suspended') },
              { value: 'archived', label: t('master.archived') },
            ]}
          />
          <FilterMenu<SoortFilter>
            label={t('tenants.kind')}
            value={soort}
            onChange={setSoort}
            options={[
              { value: 'all', label: t('beheer.platform.filters.all') },
              { value: 'customer', label: t('beheer.platform.kinds.customer') },
              { value: 'shared', label: t('beheer.platform.kinds.shared') },
              { value: 'personal', label: t('beheer.platform.kinds.personal') },
              { value: 'archived', label: t('beheer.platform.kinds.archived') },
            ]}
          />
          {/* Het type organisatie (ORG-1): fabriek, vereniging, school ... */}
          <FilterMenu<TypeFilter>
            label={t('beheer.orgType.column')}
            value={orgType}
            onChange={setOrgType}
            options={[
              { value: 'all', label: t('beheer.platform.filters.all') },
              ...ORG_TYPES.map((type) => ({
                value: type,
                label: t(`beheer.orgType.types.${type}`),
              })),
            ]}
          />
        </Toolbar>

        {tenants.isPending ? (
          <LoadingState rows={4} label={t('common.loading')} />
        ) : tenants.isError ? (
          <div className="a-card-pad">
            <Note tone="danger" role="alert">
              {t('errors.generic')}
            </Note>
          </div>
        ) : (tenants.data?.length ?? 0) === 0 && !search ? (
          <Empty
            icon={<AdminIcon name="building" size={24} />}
            title={t('tenants.empty')}
            text={t('tenants.emptyHint')}
          />
        ) : zichtbaar.length === 0 ? (
          <Empty
            icon={<AdminIcon name="search" size={24} />}
            title={t('beheer.platform.tenants.noMatch')}
            text={t('beheer.platform.tenants.noMatchHint')}
          />
        ) : (
          <Table label={t('tenants.title')}>
            <thead>
              <tr>
                <th>{t('tenants.name')}</th>
                <th>{t('tenants.code')}</th>
                <th>{t('tenants.status')}</th>
                <th>{t('tenants.kind')}</th>
                <th>{t('beheer.orgType.column')}</th>
                <th>{t('master.billing.plan')}</th>
                <th className="num">{t('master.list.users')}</th>
                <th className="num">{t('master.list.active7d')}</th>
                <th>{t('tenants.owner')}</th>
                <th>{t('tenants.lastActivity')}</th>
                <th>{t('tenants.created')}</th>
              </tr>
            </thead>
            <tbody>
              {zichtbaar.map((tenant) => (
                <tr key={tenant.id}>
                  <td>
                    <Link
                      to="/master/organisaties/$tenantId"
                      params={{ tenantId: tenant.id }}
                      className="a-name a-link"
                    >
                      {tenant.name}
                    </Link>
                  </td>
                  <td>
                    <span className="a-mono">{tenant.code}</span>
                  </td>
                  <td>
                    <Badge
                      tone={
                        tenant.status === 'active'
                          ? 'ok'
                          : tenant.status === 'archived'
                            ? 'neutral'
                            : 'attention'
                      }
                    >
                      {tenant.status === 'active'
                        ? t('tenants.active')
                        : tenant.status === 'archived'
                          ? t('master.archived')
                          : t('tenants.suspended')}
                    </Badge>
                  </td>
                  {/* Soort, eigenaar en laatste activiteit (ACC-3): welk testbedrijf van wie is,
                      en of er nog iemand in werkt. Een gewone klant is gewone tekst. */}
                  <td data-testid="tenant-kind">
                    {tenant.kind ? (
                      <Badge tone="navy">{soortLabel(tenant.kind, t)}</Badge>
                    ) : (
                      <span className="a-muted">{soortLabel(null, t)}</span>
                    )}
                  </td>
                  <td data-testid="tenant-org-type">
                    {t(`beheer.orgType.types.${tenant.orgType}`)}
                  </td>
                  <td>{perTenant.get(tenant.id)?.plan?.name ?? '—'}</td>
                  <td className="num">
                    {perTenant.get(tenant.id) ? <UsersCell row={perTenant.get(tenant.id)!} /> : '—'}
                  </td>
                  <td className="num">{perTenant.get(tenant.id)?.stats?.active7d ?? '—'}</td>
                  <td>{tenant.ownerEmail ?? '—'}</td>
                  <td>{wanneer(tenant.lastActivityAt, t)}</td>
                  <td>{maandJaar(tenant.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {/* Alleen gemount als het paneel open is: zo begint elk nieuw bedrijf met lege velden. */}
      {drawerOpen ? (
        <NewTenantDrawer
          onClose={() => setDrawerOpen(false)}
          busy={create.isPending}
          failed={create.isError}
          onSubmit={(input) => create.mutate(input)}
        />
      ) : null}
      {toast.element}
    </AdminPage>
  );
}

interface NewTenantInput {
  code: string;
  name: string;
  defaultLanguage: string;
  kind: TenantKind | null;
  ownerEmail: string | null;
  orgType: OrgType;
}

/** Wat het paneel aanmaakt: de organisatie, en optioneel de eerste vestiging en beheerder. */
interface NewTenantPlan {
  tenant: NewTenantInput;
  site: { code: string; name: string; address: string } | null;
  admin: { email: string; displayName: string } | null;
}

/**
 * "Bedrijf toevoegen" in een paneel: naam, bedrijfscode (vast na aanmaken), standaardtaal en
 * soort. Bij een persoonlijk demobedrijf ook de eigenaar, zoals de API dat al kende (ACC-3).
 */
function NewTenantDrawer({
  onClose,
  busy,
  failed,
  onSubmit,
}: {
  onClose: () => void;
  busy: boolean;
  failed: boolean;
  onSubmit: (plan: NewTenantPlan) => void;
}) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [language, setLanguage] = useState('nl');
  const [kind, setKind] = useState<'customer' | TenantKind>('customer');
  const [owner, setOwner] = useState('');
  const [orgType, setOrgType] = useState<OrgType>('factory');

  // MST-3: de eerste vestiging en de eerste beheerder, allebei optioneel.
  const [siteName, setSiteName] = useState('');
  const [siteCode, setSiteCode] = useState('');
  const [siteAddress, setSiteAddress] = useState('');
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');

  const ownerOk = kind !== 'personal' || owner.trim() === '' || owner.includes('@');
  const wilSite = siteName.trim() !== '' || siteCode.trim() !== '';
  const siteOk = !wilSite || (siteName.trim().length >= 2 && siteCode.trim().length >= 2);
  const wilAdmin = adminName.trim() !== '' || adminEmail.trim() !== '';
  const adminOk = !wilAdmin || (adminName.trim().length >= 2 && adminEmail.includes('@'));
  const klaar = code.trim().length >= 3 && name.trim().length >= 2 && ownerOk && siteOk && adminOk;
  const verstuur = () => {
    if (!klaar || busy) return;
    onSubmit({
      tenant: {
        code: code.trim(),
        name: name.trim(),
        defaultLanguage: language,
        kind: kind === 'customer' ? null : kind,
        ownerEmail: kind === 'personal' && owner.trim() ? owner.trim() : null,
        orgType,
      },
      site: wilSite
        ? { code: siteCode.trim(), name: siteName.trim(), address: siteAddress.trim() }
        : null,
      admin: wilAdmin ? { email: adminEmail.trim(), displayName: adminName.trim() } : null,
    });
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title={t('tenants.new')}
      footer={
        <>
          <Btn variant="sec" onClick={onClose}>
            {t('common.cancel')}
          </Btn>
          <Btn disabled={!klaar || busy} onClick={verstuur}>
            {t('tenants.new')}
          </Btn>
        </>
      }
    >
      <Form onSubmit={verstuur}>
        <Field label={t('tenants.name')}>
          {(props) => (
            <Input
              {...props}
              value={name}
              required
              onChange={(event) => setName(event.target.value)}
            />
          )}
        </Field>
        <Field
          label={t('tenants.code')}
          hint={t('tenants.codeHint')}
          error={failed ? t('tenants.createFailed') : undefined}
        >
          {(props) => (
            <Input
              {...props}
              className="a-mono"
              value={code}
              maxLength={12}
              required
              onChange={(event) => setCode(event.target.value.toLowerCase())}
            />
          )}
        </Field>
        <Field label={t('tenants.language')}>
          {(props) => (
            <Select
              {...props}
              value={language}
              onChange={(event) => setLanguage(event.target.value)}
            >
              <option value="nl">Nederlands</option>
              <option value="fr">Français</option>
              <option value="en">English</option>
            </Select>
          )}
        </Field>
        <Field label={t('beheer.orgType.label')} hint={t('beheer.orgType.createHint')}>
          {(props) => (
            <Select
              {...props}
              data-testid="new-tenant-org-type"
              value={orgType}
              onChange={(event) => setOrgType(event.target.value as OrgType)}
            >
              {ORG_TYPES.map((type) => (
                <option key={type} value={type}>
                  {t(`beheer.orgType.types.${type}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={t('tenants.kind')} hint={t('beheer.platform.tenants.kindHint')}>
          {(props) => (
            <Select
              {...props}
              value={kind}
              onChange={(event) => setKind(event.target.value as typeof kind)}
            >
              <option value="customer">{t('beheer.platform.kinds.customer')}</option>
              <option value="shared">{t('beheer.platform.kinds.shared')}</option>
              <option value="personal">{t('beheer.platform.kinds.personal')}</option>
              <option value="archived">{t('beheer.platform.kinds.archived')}</option>
            </Select>
          )}
        </Field>
        {kind === 'personal' ? (
          <Field
            label={t('tenants.owner')}
            optional
            hint={t('beheer.platform.tenants.ownerHint')}
            error={ownerOk ? undefined : t('beheer.platform.tenants.ownerInvalid')}
          >
            {(props) => (
              <Input
                {...props}
                type="email"
                value={owner}
                onChange={(event) => setOwner(event.target.value)}
              />
            )}
          </Field>
        ) : null}

        <h3 className="a-h3">{t('master.create.siteTitle')}</h3>
        <p className="a-muted">{t('master.create.siteHint')}</p>
        <Field label={t('master.create.siteName')} optional>
          {(props) => (
            <Input
              {...props}
              data-testid="new-tenant-site-name"
              value={siteName}
              onChange={(event) => setSiteName(event.target.value)}
            />
          )}
        </Field>
        <Field
          label={t('master.create.siteCode')}
          optional
          hint={t('master.create.siteCodeHint')}
          error={siteOk ? undefined : t('master.create.siteIncomplete')}
        >
          {(props) => (
            <Input
              {...props}
              className="a-mono"
              maxLength={12}
              value={siteCode}
              onChange={(event) => setSiteCode(event.target.value.toUpperCase())}
            />
          )}
        </Field>
        <Field label={t('master.sites.address')} optional>
          {(props) => (
            <Input
              {...props}
              value={siteAddress}
              onChange={(event) => setSiteAddress(event.target.value)}
            />
          )}
        </Field>

        <h3 className="a-h3">{t('master.create.adminTitle')}</h3>
        <p className="a-muted">{t('master.create.adminHint')}</p>
        <Field label={t('master.create.adminName')} optional>
          {(props) => (
            <Input
              {...props}
              data-testid="new-tenant-admin-name"
              value={adminName}
              onChange={(event) => setAdminName(event.target.value)}
            />
          )}
        </Field>
        <Field
          label={t('master.create.adminEmail')}
          optional
          error={adminOk ? undefined : t('master.create.adminIncomplete')}
        >
          {(props) => (
            <Input
              {...props}
              type="email"
              data-testid="new-tenant-admin-email"
              value={adminEmail}
              onChange={(event) => setAdminEmail(event.target.value)}
            />
          )}
        </Field>
      </Form>
    </Drawer>
  );
}
